import React, { createContext, useContext, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

interface LanguageContextType {
  currentLanguage: string;
  toggleLanguage: () => void;
  showLanguagePopup: boolean;
  setShowLanguagePopup: (show: boolean) => void;
  changeLanguage: (lang: string) => void;
}

const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [currentLanguage, setCurrentLanguage] = useState(i18n.language);
  const [showLanguagePopup, setShowLanguagePopup] = useState(false);

  const toggleLanguage = () => {
    const newLanguage = currentLanguage === 'en' ? 'id' : 'en';
    changeLanguage(newLanguage);
  };

  const changeLanguage = (lang: string) => {
    i18n.changeLanguage(lang);
    setCurrentLanguage(lang);
    localStorage.setItem('language', lang);
    localStorage.setItem('language-selected', 'true');
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('language');
    const hasSelectedLanguage = localStorage.getItem('language-selected');
    
    if (savedLanguage && savedLanguage !== currentLanguage) {
      i18n.changeLanguage(savedLanguage);
      setCurrentLanguage(savedLanguage);
    }

    // Show popup if user hasn't selected language before
    if (!hasSelectedLanguage) {
      const timer = setTimeout(() => {
        setShowLanguagePopup(true);
      }, 1000); // Show popup after 1 second

      return () => clearTimeout(timer);
    }
  }, [currentLanguage, i18n]);

  return (
    <LanguageContext.Provider value={{ 
      currentLanguage, 
      toggleLanguage, 
      showLanguagePopup, 
      setShowLanguagePopup,
      changeLanguage 
    }}>
      {children}
    </LanguageContext.Provider>
  );
};

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (context === undefined) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
};